import { Plus } from '@griply/assets';
import {
  ExternalCalendarEvent,
  overdueSectionID,
  Task,
  Timestamp,
} from '@griply/types';
import { getCalendarEventReferenceDate, isBetween } from '@griply/utils';
import { ItemInstance } from '@headless-tree/core';
import Typography from '@mui/material/Typography';
import {
  differenceInDays,
  endOfMonth,
  format,
  getDate,
  startOfDay,
} from 'date-fns';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft } from 'shared/assets/icons';
import { RescheduleButton } from 'shared/components/connected/reschedule-button';
import { ExternalEventsList } from 'shared/components/ui/external-events-list';
import { Colors, IconButton } from 'shared/components/ui/icon-button';
import { ListSectionDateHeader } from 'shared/components/ui/list-section-date-header';
import { ListSectionMonthHeader } from 'shared/components/ui/list-section-month-header';
import { ListSectionYearHeader } from 'shared/components/ui/list-section-year-header';
import { InternalTreeItem } from 'shared/components/ui/virtualized-tree-list';
import { useToday } from 'shared/contexts/today';
import { CalendarSlotMenuAnchor } from 'shared/hooks/use-calendar-slot-menu';
import { TimeFormatOptions } from 'shared/types/date-format-options';

import * as DeadlineStyled from '../deadline-header/deadline-header.style';
import * as Styled from './upcoming-deadline-header.style';
import {
  getSectionLabel,
  ParsedSection,
  parseSectionId,
  SectionType,
} from './upcoming-deadline-section';

const filterEventsForSection = (
  events: ExternalCalendarEvent[],
  startDate: Timestamp,
  endDate: Timestamp,
): ExternalCalendarEvent[] =>
  events.filter((event) => {
    const eventDate = getCalendarEventReferenceDate(event.start, startDate);
    return isBetween(eventDate, startDate, endDate);
  });

type SectionTitleProps = {
  section: ParsedSection;
  name: string;
};

const SectionTitle: React.FC<SectionTitleProps> = ({ section, name }) => {
  const { t } = useTranslation();

  if (section.type === SectionType.Overdue) {
    return (
      <Typography variant="h6" component="span">
        {t('general.sections.overdue.title')}
      </Typography>
    );
  }

  if (section.type === SectionType.Day && section.date) {
    return <ListSectionDateHeader date={section.date} />;
  }

  if (section.type === SectionType.RestOfMonth && section.date) {
    const romStart = startOfDay(section.date);
    const romEnd = endOfMonth(section.date);
    const oneDayLeft = differenceInDays(romEnd, romStart) <= 0;

    return (
      <Styled.RestOfMonthContainer>
        <Typography variant="h6" component="span">
          {t('general.labels.rest-of-month.title', {
            month: format(romStart, 'LLLL'),
          })}
        </Typography>
        <Typography variant="h6" component="span" color="text.secondary">
          {oneDayLeft
            ? format(romStart, 'd MMM')
            : t('general.labels.rest-of-days.title', {
                start: getDate(romStart),
                end: getDate(romEnd),
              })}
        </Typography>
      </Styled.RestOfMonthContainer>
    );
  }

  if (section.type === SectionType.Month && section.date) {
    return <ListSectionMonthHeader date={section.date} />;
  }

  if (section.type === SectionType.Year && section.date) {
    return <ListSectionYearHeader date={section.date} />;
  }

  return (
    <Typography variant="h6" component="span">
      {name}
    </Typography>
  );
};

export type UpcomingDeadlineHeaderProps = {
  item: ItemInstance<InternalTreeItem>;
  denseFormat?: boolean;
  overdueTasks?: Task[];
  externalEvents?: ExternalCalendarEvent[];
  timeFormat?: TimeFormatOptions;
  onAddForDate?: (date: Timestamp, anchor: CalendarSlotMenuAnchor) => void;
};

export const UpcomingDeadlineHeader: React.FC<UpcomingDeadlineHeaderProps> = ({
  item,
  denseFormat,
  overdueTasks,
  externalEvents,
  timeFormat,
  onAddForDate,
}) => {
  const { t } = useTranslation();
  const today = useToday();
  const sectionId = item.getItemData()?.sourceId;
  const isOverdue = sectionId === overdueSectionID;
  const isExpanded = item.isExpanded();

  const section = useMemo(
    () =>
      sectionId
        ? parseSectionId(sectionId)
        : {
            type: SectionType.Day,
            date: undefined,
            startDate: undefined,
            endDate: undefined,
          },
    [sectionId],
  );

  const label = useMemo(
    () => getSectionLabel(section, today, t),
    [section, today, t],
  );

  const sectionEvents = useMemo(() => {
    if (!externalEvents?.length || !section.startDate || !section.endDate) {
      return [];
    }
    return filterEventsForSection(
      externalEvents,
      section.startDate,
      section.endDate,
    );
  }, [externalEvents, section.startDate, section.endDate]);

  const toggleOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    isExpanded ? item.collapse() : item.expand();
  };

  // the row click toggles the section; an event click must only open the event
  const stopPropagation = (e: React.MouseEvent) => e.stopPropagation();

  const handleAdd = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (!section.date || !onAddForDate) {
      return;
    }
    const { bottom, left } = e.currentTarget.getBoundingClientRect();
    onAddForDate(section.date, { top: bottom, left });
  };

  if (!sectionId) {
    return null;
  }

  const canAdd =
    section.type === SectionType.Day && !!section.date && !!onAddForDate;

  return (
    <Styled.SectionContainer>
      <DeadlineStyled.HeaderContainer $denseFormat={denseFormat}>
        <SectionTitle section={section} name={item.getItemData().name} />

        <Styled.Actions>
          {isOverdue && !!overdueTasks?.length && (
            <RescheduleButton tasks={overdueTasks} />
          )}
          {canAdd && (
            <IconButton
              icon={Plus}
              strokeWidth={2}
              onClick={handleAdd}
              color={Colors.Default}
              aria-label={t('general.menu.navigation.add-to-section', {
                label,
              })}
            />
          )}
          <IconButton
            icon={ChevronLeft}
            onClick={toggleOpen}
            rotation={isExpanded ? -90 : 0}
            color={Colors.Default}
            aria-expanded={isExpanded}
            aria-label={t(
              isExpanded
                ? 'general.menu.navigation.collapse-section'
                : 'general.menu.navigation.expand-section',
              { label },
            )}
          />
        </Styled.Actions>
      </DeadlineStyled.HeaderContainer>

      {isExpanded && !!sectionEvents.length && timeFormat && (
        <Styled.ExternalEventsContainer onClick={stopPropagation}>
          <ExternalEventsList
            events={sectionEvents}
            timeFormat={timeFormat}
            show
          />
        </Styled.ExternalEventsContainer>
      )}
    </Styled.SectionContainer>
  );
};
